import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Package, ArrowRight } from "lucide-react";
import { api, inr } from "@/lib/api";

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Order Confirmed — Dignity Food Kitchen";
    api.get(`/orders/${orderNumber}`).then(({ data }) => setOrder(data.order)).catch(() => {});
  }, [orderNumber]);

  return (
    <div className="grid min-h-screen place-items-center bg-black px-5 pt-24">
      <div className="w-full max-w-2xl pb-20">
        <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className="mx-auto grid h-24 w-24 place-items-center rounded-full gold-gradient" data-testid="order-success-icon">
          <CheckCircle2 size={52} className="text-black" />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="mt-8 text-center">
          <h1 className="font-display text-5xl tracking-tight text-cream sm:text-7xl">ORDER CONFIRMED!</h1>
          <p className="mt-4 text-white/60">
            Thank you for snacking smart. Your crunch is on its way.
          </p>
          {order?.simulated && (
            <p className="mt-2 inline-block rounded-full bg-gold/15 px-4 py-1 text-xs text-gold">
              Test mode — no real payment was charged
            </p>
          )}
        </motion.div>

        {order && (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            className="mt-10 rounded-3xl border border-white/10 bg-forest-deep p-7">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center gap-2">
                <Package size={18} className="text-gold" />
                <span className="font-oswald uppercase tracking-wide text-cream">Order {order.order_number}</span>
              </div>
              <span className="rounded-full bg-green-500/15 px-3 py-1 text-xs font-semibold uppercase text-green-400">
                {order.status}
              </span>
            </div>

            <div className="mt-5 space-y-3">
              {order.items.map(({ product, qty }) => (
                <div key={product.id} className="flex items-center gap-3">
                  <img src={product.image} alt={product.name} className="h-14 w-11 rounded-lg object-cover" />
                  <div className="flex-1">
                    <div className="font-oswald text-sm text-cream">{product.name}</div>
                    <div className="text-xs text-white/40">× {qty}</div>
                  </div>
                  <span className="font-oswald text-sm text-cream">{inr(product.price * qty)}</span>
                </div>
              ))}
            </div>

            <div className="mt-5 space-y-1.5 border-t border-white/10 pt-4 text-sm">
              <Row label="Subtotal" value={inr(order.totals.subtotal)} />
              {order.totals.discount > 0 && <Row label="Discount" value={`- ${inr(order.totals.discount)}`} />}
              <Row label="Delivery" value={order.totals.delivery === 0 ? "FREE" : inr(order.totals.delivery)} />
              <div className="flex items-center justify-between pt-2">
                <span className="font-oswald text-lg text-cream">TOTAL PAID</span>
                <span className="font-display text-2xl text-gold">{inr(order.totals.total)}</span>
              </div>
            </div>

            <div className="mt-5 rounded-2xl bg-white/[0.03] p-4 text-sm text-white/50">
              Shipping to <span className="text-cream">{order.address.full_name}</span>, {order.address.line1},
              {order.address.city}, {order.address.state} — {order.address.pincode}
            </div>
          </motion.div>
        )}

        <div className="mt-8 text-center">
          <Link to="/shop" data-testid="continue-shopping-btn"
            className="group inline-flex items-center gap-2 rounded-full gold-gradient px-8 py-4 font-oswald text-sm uppercase tracking-[0.15em] text-black transition-transform hover:scale-105">
            Continue Shopping <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-white/60"><span>{label}</span><span className="text-cream">{value}</span></div>
);
